import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  actionOpenJobStartAtDialog,
  actionCloseJobStartAtDialog,
} from 'lib/actions';
import { JobStartAtDialogEventEmitter } from 'components/JobStartAtDialog';
import { getJobTimePromptEnabled } from 'lib/selectors/getJobTimePromptEnabled';
import moment from 'moment';

/**
 * Custom hook to manage the job start date selection dialog
 * @returns {Function} A function that returns a Promise resolving with the selected date or rejecting if cancelled
 */
const useRequestJobStartAt = () => {
  const dispatch = useDispatch();

  const isJobTimePromptEnabled = useSelector(getJobTimePromptEnabled);

  /**
   * Opens the job start date selection dialog with the provided options
   * @param {Object} options - Configuration options for the dialog
   * @returns {Object} The dispatched action
   */
  const openJobStartAtDialog = useCallback(() => {
    return dispatch(actionOpenJobStartAtDialog());
  }, [dispatch]);

  /**
   * Closes the job start date selection dialog
   * @returns {Object} The dispatched action
   */
  const closeJobStartAtDialog = useCallback(() => {
    return dispatch(actionCloseJobStartAtDialog());
  }, [dispatch]);

  /**
   * Request job start date selection
   * @returns {Promise<string>} Promise that resolves with the selected date or rejects if cancelled
   */
  return useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!isJobTimePromptEnabled) {
        const nowISO = moment().toISOString();
        resolve(nowISO);
        return;
      }

      JobStartAtDialogEventEmitter.init();

      JobStartAtDialogEventEmitter.on('success', (selectedDate) => {
        closeJobStartAtDialog();
        resolve(selectedDate);
      });

      JobStartAtDialogEventEmitter.on('cancel', () => {
        closeJobStartAtDialog();
        reject(new Error('Job start time selection was cancelled'));
      });

      openJobStartAtDialog();
    });
  }, [openJobStartAtDialog, closeJobStartAtDialog, isJobTimePromptEnabled]);
};

export default useRequestJobStartAt;
